export interface Experience {
  title: string
  company: string
  location: string
  period: string
  description: string
  technologies: string[]
  link: string
}

export interface Project {
  title: string
  description: string
  image: string
  technologies: string[]
  github?: string
  live: string
  featured: boolean
}

export interface Skill {
  name: string
  description: string
  icon: string
}

export interface ContactInfo {
  icon: string
  title: string
  value: string
  link: string
}

export interface SocialLink {
  name: string
  url: string
  icon: string
  color: string
}

// Personal Information
export const personalInfo = {
  name: 'Abhishek Kurmi',
  title: 'iOS Developer',
  subtitle: 'Passionate iOS Developer with expertise in Swift, UIKit, and mobile app development',
  email: 'itsabhi814@gmail.com',
  phone: '(+91) 6353548814',
  location: 'India',
  linkedin: 'https://www.linkedin.com/in/abhishek-kurmi-ak27/',
  github: 'https://github.com/Abhishek6353',
  resume: '/Abhishek_Resume.pdf'
}

// About Section
export const aboutContent = {
  intro: "I'm a passionate iOS Developer with expertise in Swift, UIKit, and mobile app development. I love creating user-friendly, performance-oriented applications that make a difference in people's lives.",
  journey: [
    "With several years of experience in iOS development, I've successfully developed and launched multiple applications on the App Store. My passion lies in creating scalable, maintainable code that delivers exceptional user experiences.",
    "I specialize in Swift programming, UIKit framework, and modern iOS development practices. I have strong expertise in RESTful APIs, Firebase integration, and implementing complex features like In-App Purchases and third-party integrations.",
    "When I'm not coding, you can find me exploring new iOS technologies, contributing to open-source projects, or sharing knowledge with the developer community."
  ],
  stats: [
    { number: '3+', label: 'Years Experience' },
    { number: '7+', label: 'Apps Developed' }
   //{ number: '10+', label: 'Technologies Mastered' }
  ]
}

// Skills
export const skills: Skill[] = [
  {
    name: 'iOS Development',
    description: 'Swift, UIKit, Core Data, User Defaults',
    icon: 'Smartphone'
  },
  {
    name: 'Networking & APIs',
    description: 'RESTful APIs, JSON, XML, Alamofire',
    icon: 'Globe'
  },
  {
    name: 'App Store & Monetization',
    description: 'In-App Purchase, Ads Integration, App Store',
    icon: 'Zap'
  },
  {
    name: 'Development Tools',
    description: 'Git, Xcode, Debugging, Troubleshooting',
    icon: 'Code'
  },
  {
    name: 'Architecture & Patterns',
    description: 'MVC, MVVM, Multithreading, Concurrency',
    icon: 'Database'
  },
  {
    name: 'Third-party Integration',
    description: 'Firebase, SDWebImage, SnapKit, AVFoundation',
    icon: 'Shield'
  }
]

// Experience
export const experiences: Experience[] = [
  {
    title: 'iOS Developer',
    company: 'Derived Web Technologies Pvt. Ltd.',
    location: 'India',
    period: 'Aug ’24 - Current',
    description: 'Successfully resolved technical issues, implemented new features, and launched two applications on the App Store.\nCollaborated with iOS and cross-functional teams to develop user-friendly, performance-oriented applications.',
    technologies: ['Swift', 'UIKit', 'REST APIs', 'In-app Purchase'],
    link: 'https://derivedweb.com/'
  },
  {
    title: 'Jr. iOS Developer',
    company: 'Silversky Technology',
    location: 'India',
    period: 'Jan ’23 - July ’24',
    description: 'Developed and maintained 4+ high-performance iOS applications using Swift.\n Resolved technical issues and optimized code to improve app functionality.\nContributed across software development lifecycle, from concept to deployment.',
    technologies: ['Swift', 'UIKit', 'REST APIs', 'Firebase', 'SnapKit', 'AVFoundation', 'In-App Purchase', 'Add Integration'],
    link: 'https://silverskytechnology.com/'
  },
  {
    title: 'iOS Developer Intern',
    company: 'Silversky Technology',
    location: 'India',
    period: 'July ’22 - Jan ’23',
    description: 'Expertise in Swift and proficient with UIKit for responsive app development.\nIntegrated APIs and Firebase services to enhance functionality and insights.\nDesigned and prototyped 1 iOS app from concept to launch.',
    technologies: ['Swift', 'UIKit', 'REST APIs', 'Firebase'],
    link: 'https://silverskytechnology.com/'
  }
]

// Projects
export const projects: Project[] = [
  {
    title: 'Logos: Coptic Christian Dating',
    description: 'A Christian dating app dedicated to fostering God-centered relationships. Find like-minded individuals who share your faith and values, and build a strong foundation for a lasting partnership.',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/3a/bf/f3/3abff3d5-0d46-a916-fb6f-5b02bf1fafbc/AppIcon-0-0-1x_U007emarketing-0-11-0-sRGB-0-85-220.png/512x512bb.jpg',
    technologies: ['Swift', 'UIKit', 'REST APIs', 'Firebase'],
    live: 'https://apps.apple.com/us/app/logos-christian-dating-app/id6477770277',
    featured: true
  },
  {
    title: 'Smart AI Chatbot & Assistant',
    description: 'A powerful AI-driven virtual assistant app built using ChatGPT-4, designed to help users complete tasks quickly and intelligently through natural language prompts. Key features include smart Q&A, content writing (essays, emails, stories), image generation from text, real-time language translation, and instant, accurate responses. With a modern, user-friendly UI and community sharing options, it enhances productivity, creativity, and learning for professionals, students, and creators across various domains.',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e5/a3/27/e5a327f0-f76d-8f33-e456-424b58bea6d9/AppIcon-0-0-1x_U007emarketing-0-10-0-85-220.png/512x512bb.jpg',
    technologies: ['Swift', 'UIKit', 'API Integration', 'ChatGPT API', 'Core Data'],
    live: 'https://apps.apple.com/us/app/smart-ai-chatbot-assistant/id6449279097',
    featured: true
  },
  {
    title: 'DoublePi',
    description: 'DoublePi is a social networking app inspired by the mathematical constant 2π, symbolizing a circular, sustainable society. The platform provides a secure space for users to connect, share, and exchange free, used, or unused goods, promoting eco-friendly practices and community engagement. Users can showcase their DIY projects, participate in themed contests, and access a feed (2PI Feed) featuring ideas for earth-friendly living. The app supports multiple languages and is available on iPhone, and iPad devices.',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/16/1b/22/161b2298-9448-38d3-487d-c6e78816444e/AppIcon-1x_U007emarketing-0-5-0-sRGB-85-220.png/512x512bb.jpg',
    technologies: ['Swift', 'UIKit', 'Firebase', 'In-App Purchase', 'Firebase Realtime Database'],
    live: 'https://apps.apple.com/us/app/doublepi/id1323581363',
    featured: true
  },
  {
    title: 'GM Klang Business Community',
    description: 'The GM Klang Business Community app is designed to streamline communication and business activity within Malaysia\'s largest wholesale center. It connects wholesalers, traders, and customers, offering features such as digital directories, promotional updates, event information, and community engagement tools. The app enhances accessibility and supports the wholesale business ecosystem with timely updates and essential business tools.',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/78/14/93/78149389-c6df-a9dc-be56-cc89942a7cde/AppIcon-0-0-1x_U007emarketing-0-10-0-85-220.png/512x512bb.jpg',
    technologies: ['Swift', 'UIKit', 'QR & Barcode Scanner', 'Bug Fix', 'AV Foundation'],
    live: 'https://apps.apple.com/my/app/gm-klang-business-community/id1594071573',
    featured: true
  }
]

// Contact Information
export const contactInfo: ContactInfo[] = [
  {
    icon: 'Mail',
    title: 'Email',
    value: personalInfo.email,
    link: `mailto:${personalInfo.email}`
  },
  {
    icon: 'Phone',
    title: 'Phone',
    value: personalInfo.phone,
    link: `tel:${personalInfo.phone.replace(/\s/g, '')}`
  },
  {
    icon: 'MapPin',
    title: 'Location',
    value: personalInfo.location,
    link: '#'
  }
]

// Social Links
export const socialLinks: SocialLink[] = [
  {
    name: 'LinkedIn',
    url: personalInfo.linkedin,
    icon: 'Linkedin',
    color: 'hover:text-blue-600'
  },
  {
    name: 'GitHub',
    url: personalInfo.github,
    icon: 'Github',
    color: 'hover:text-gray-900 dark:hover:text-white'
  }
] 
